import { useState, useEffect, useCallback } from 'preact/hooks'
import { parseSpotsDisplay, widgetSpotsText } from '../../core/spots-display'
import { apiClient, authStore, InitData } from '../../core'
import { t, intlLocale, formatMoney, plural } from '../../core/i18n'
import { fmtDag, fmtTid, studietsTidszone } from '../../core/tidszone'
import { classPriceDisplay } from '../../core/members-only'
import { BookingModal, type BookableClass } from '../../components/BookingModal'

interface UpcomingClass {
  /** Fri holdtype på den indloggedes abonnement (OPG-0079): vis «Med i dit abonnement». */
  includedInSubscription?: boolean
  id: string
  name: string
  description: string | null
  startTime: string
  endTime: string
  instructorName: string | null
  currentParticipants: number
  maxParticipants: number
  room: string | null
  dropInPriceDkk: number
  waitlistEnabled?: boolean
  membershipRequired?: boolean
  excludeFromMembership?: boolean
  /** «Gratis hold» (OPG-0098): ingen betaling, aldrig et klip. */
  isFree?: boolean
  clipCost?: number
  classTemplateId?: string | null
  guestsAllowed?: boolean
  maxGuests?: number
  fullVenueAvailable?: boolean
  fullVenuePriceDkk?: number | null
  /** Holdets egen afmeldingsfrist i timer (null = den generelle), fra widget-API'et. */
  ownCancellationHours?: number | null
  isBooked?: boolean
}

interface Props {
  initData: InitData
  limit?: number
  title?: string | null
  /** guests-only="true": gæste-fokuseret booking (stepperen vises åben). */
  guestsOnly?: boolean
}

// Holdets dag og tid i studiets tidszone, ikke den besøgendes browser (QA 27-10).
function fmtDay(iso: string, locale: string, tz: string): string {
  return fmtDag(iso, locale, tz, { weekday: 'short', day: 'numeric', month: 'short' })
}
function fmtTime(iso: string, locale: string, tz: string): string {
  return fmtTid(iso, locale, tz)
}

/**
 * UpcomingClassesWidget — card grid of next N classes.
 * Mirrors HS-native upcoming-classes section.
 */
export function UpcomingClassesWidget({ initData, limit = 6, title = null, guestsOnly = false }: Props) {
  const str = t(initData.locale)
  const spotsMode = parseSpotsDisplay(initData.features?.spotsDisplay)
  const dateLocale = intlLocale(initData.locale)
  const tz = studietsTidszone(initData.timezone)
  const [classes, setClasses] = useState<UpcomingClass[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<UpcomingClass | null>(null)
  const safeLimit = Math.max(1, Math.min(50, limit))

  const fetchClasses = useCallback(async () => {
    // Try authenticated first so the API can mark isBooked classes. If the
    // token refresh fails (stale session), fall back to anonymous — the
    // widget should still show classes, just without per-user markers.
    const useAuth = authStore.isLoggedIn()
    let result = await apiClient.get<UpcomingClass[]>(`/api/widget/upcoming-classes?limit=${safeLimit}`, useAuth)
    if (useAuth && result.code === 'AUTH_REQUIRED') {
      result = await apiClient.get<UpcomingClass[]>(`/api/widget/upcoming-classes?limit=${safeLimit}`)
    }
    if (result.data) setClasses(result.data)
    else if (result.error) setError(result.error)
    setLoading(false)
  }, [safeLimit])

  useEffect(() => {
    fetchClasses()
  }, [fetchClasses])

  // Refresh data + clear selection after a successful booking
  const handleBooked = () => {
    fetchClasses()
  }

  if (loading) {
    return (
      <div>
        <style>{styles}</style>
        <div class="hsw-up-grid">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} class="hsw-up-card">
              <div class="hsw-skel hsw-up-skel-meta" />
              <div class="hsw-skel hsw-up-skel-name" />
              <div class="hsw-skel hsw-up-skel-line" />
              <div class="hsw-skel hsw-up-skel-line" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <style>{styles}</style>
        <div role="alert" style={{ padding: '1rem 1.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--yoga-radius, 0)', color: '#b91c1c', maxWidth: '600px', margin: '0 auto' }}>
          <strong>⚠️ {error}</strong>
        </div>
      </div>
    )
  }

  if (!classes.length) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📅</div>
        <p>{str.noUpcomingClasses}</p>
      </div>
    )
  }

  const modalKlass: BookableClass | null = selected ? {
    id: selected.id,
    name: selected.name,
    startTime: selected.startTime,
    endTime: selected.endTime,
    instructorName: selected.instructorName,
    room: selected.room,
    description: selected.description,
    maxParticipants: selected.maxParticipants,
    currentParticipants: selected.currentParticipants,
    dropInPriceDkk: selected.dropInPriceDkk,
    waitlistEnabled: selected.waitlistEnabled,
    membershipRequired: selected.membershipRequired,
    excludeFromMembership: selected.excludeFromMembership,
    isFree: selected.isFree,
    clipCost: selected.clipCost,
    classTemplateId: selected.classTemplateId,
    guestsAllowed: selected.guestsAllowed,
    maxGuests: selected.maxGuests,
    fullVenueAvailable: selected.fullVenueAvailable,
    fullVenuePriceDkk: selected.fullVenuePriceDkk,
    // Uden denne linje fik booking-vinduet aldrig fristen, og «Særlig afmeldingsfrist»
    // blev aldrig vist i den indlejrede widget (fundet af /ship 14-10-2026).
    ownCancellationHours: selected.ownCancellationHours,
    isBooked: selected.isBooked,
  } : null

  return (
    <div>
      <style>{styles}</style>
      {title && <h2 class="hsw-up-title">{title}</h2>}
      <div class="hsw-up-grid">
        {classes.map(cls => {
          const spotsLeft = cls.maxParticipants - cls.currentParticipants
          const isFull = spotsLeft <= 0
          const onClick = (e: Event) => {
            e.preventDefault()
            setSelected(cls)
          }
          return (
            <button
              key={cls.id}
              type="button"
              onClick={onClick}
              class={`hsw-up-card${isFull ? ' hsw-up-full' : ''}${cls.isBooked ? ' hsw-up-booked' : ''}`}
            >
              <div class="hsw-up-meta">{fmtDay(cls.startTime, dateLocale, tz)} · {fmtTime(cls.startTime, dateLocale, tz)}</div>
              <h3 class="hsw-up-name">{cls.name}</h3>
              <div class="hsw-up-row">
                {cls.instructorName && <span>👤 {cls.instructorName}</span>}
                {cls.room && <span>{cls.room}</span>}
              </div>
              <div class="hsw-up-row">
                <span class={`hsw-up-spots${isFull ? ' hsw-up-spots-full' : ''}`}>
                  {cls.isBooked ? `✓ ${str.booked}` : isFull ? (cls.waitlistEnabled ? str.waitlist : str.full) : widgetSpotsText(spotsLeft, cls.maxParticipants, spotsMode, str, () => `${spotsLeft}/${cls.maxParticipants} ${str.spots}`)}
                </span>
                <strong class="hsw-up-price">
                  {cls.includedInSubscription
                    ? str.includedInSubscription
                    : (cls.clipCost ?? 1) > 1 && !cls.excludeFromMembership && !cls.isFree
                    ? plural(str.clipCostShort, cls.clipCost ?? 0, initData.locale)
                    : classPriceDisplay(cls).kind === 'price'
                      ? formatMoney(cls.dropInPriceDkk, initData.locale, initData.currency)
                      : classPriceDisplay(cls).kind === 'membersOnly' ? str.membershipOnly : str.freeClass}
                </strong>
              </div>
            </button>
          )
        })}
      </div>
      {modalKlass && (
        <BookingModal
          klass={modalKlass}
          initData={initData}
          onClose={() => setSelected(null)}
          onBooked={handleBooked}
          guestsOnly={guestsOnly}
        />
      )}
    </div>
  )
}

const styles = `
  .hsw-up-title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    text-align: center;
    margin: 0 auto 2rem;
    max-width: 1200px;
    letter-spacing: -0.01em;
  }
  .hsw-up-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1rem;
    max-width: 1200px;
    margin: 0 auto;
  }
  .hsw-up-card {
    display: block;
    background: var(--yoga-card-bg, #fff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 1.5rem;
    text-decoration: none;
    color: inherit;
    transition: all .3s;
    cursor: pointer;
    text-align: left;
    width: 100%;
    font-family: inherit;
    font-size: inherit;
  }
  .hsw-up-card:hover {
    border-color: var(--yoga-primary, #FF6B35);
    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
    transform: translateY(-2px);
  }
  .hsw-up-card.hsw-up-full {
    opacity: 0.7;
  }
  .hsw-up-card.hsw-up-booked {
    border-color: #16a34a;
    background: rgba(22,163,74,0.04);
  }
  .hsw-up-meta {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--yoga-text-muted, #737373);
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  .hsw-up-name {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
    margin: 0 0 0.75rem;
  }
  .hsw-up-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0;
    border-bottom: 1px solid #f0f0f0;
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #525252);
  }
  .hsw-up-row:last-child {
    border-bottom: 0;
  }
  .hsw-up-spots {
    color: var(--yoga-text-muted, #525252);
    font-weight: 500;
  }
  .hsw-up-spots-full {
    color: #dc2626;
    font-weight: 700;
  }
  .hsw-up-price {
    color: var(--yoga-text, #0a0a0a);
    font-weight: 700;
  }
  /* Skeletons */
  .hsw-skel {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 0.5rem;
  }
  .hsw-up-skel-meta { height: 12px; width: 50%; }
  .hsw-up-skel-name { height: 22px; width: 70%; margin-bottom: 1rem; }
  .hsw-up-skel-line { height: 14px; width: 100%; }
  @keyframes hsw-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`
